import ApplicableIcon from '@/assets/applicable.svg?react';
import NotApplicableIcon from '@/assets/not_applicable.svg?react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/utils/classUtils';
import type { ReferenceDetailVersionUpdate } from '@ditto/shared';
import { Archive, ArrowRight, CircleDashed } from 'lucide-react';
import { type FC } from 'react';
import { PRIORITY_LABELS, STATUS_LABELS } from '../labels';
import { PriorityIcon } from './PriorityIcon';

const APPLICABILITY_ICON_MAP: Record<string, FC<{ className?: string }>> = {
  to_review: CircleDashed,
  applicable: ApplicableIcon,
  not_applicable: NotApplicableIcon,
  out_of_scope: Archive,
};

const ApplicabilityStatusIcon: FC<{ status: string | null }> = ({ status }) => {
  if (!status) return null;
  const Icon = APPLICABILITY_ICON_MAP[status];
  if (!Icon) return null;
  const label = STATUS_LABELS[status] ?? status;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0 cursor-default">
          <Icon className="size-5 text-neutral-900" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
};

const PriorityIconWithTooltip: FC<{ priority: string | null }> = ({
  priority,
}) => {
  if (!priority) return null;
  const label = PRIORITY_LABELS[priority] ?? priority;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0 cursor-default [&_img]:brightness-0">
          <PriorityIcon
            priority={priority}
            className="size-5 text-neutral-900"
          />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
};

export const ReferenceUpdateSummaryCard: FC<{
  update: ReferenceDetailVersionUpdate;
  onClick: () => void;
  /** True while pointer is over the card or a wrapping hover bridge (e.g. table tooltip). */
  interactionActive?: boolean;
  /**
   * When set, arrow color follows this flag only (gray when false, black when true).
   * Omit on reference detail so the arrow responds to card hover like other UPDATE cards.
   */
  arrowEmphasized?: boolean;
}> = ({ update, onClick, interactionActive, arrowEmphasized }) => {
  return (
    <button
      type="button"
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'group/update-card w-full text-left rounded-lg border border-neutral-200 bg-white p-3',
        'shadow-md transition-all hover:shadow-lg hover:border-neutral-300',
        interactionActive && 'shadow-lg border-neutral-300'
      )}
    >
      <div className="flex items-center gap-2.5">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span className="text-[11px] uppercase tracking-[1px] font-medium text-neutral-600">
            UPDATE
          </span>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <ApplicabilityStatusIcon status={update.applicabilityStatus} />
          <PriorityIconWithTooltip priority={update.priority} />
        </div>
      </div>
      {update.shortSummary && (
        <div className="mt-1 flex items-end gap-0.5">
          <p className="flex-1 min-w-0 text-xs text-neutral-900 leading-[1.6] tracking-[0.18px] line-clamp-2">
            {update.shortSummary}
          </p>
          <ArrowRight
            className={cn(
              'size-4 shrink-0 text-neutral-400 transition-colors',
              arrowEmphasized === undefined
                ? cn(
                    'group-hover/update-card:text-neutral-900',
                    interactionActive && 'text-neutral-900'
                  )
                : arrowEmphasized && 'text-neutral-900'
            )}
          />
        </div>
      )}
    </button>
  );
};
